import { Renderer, type GameSnapshot } from "../render/renderer";
import { createGameScreen, replaceRoot } from "../ui/ui";
import { OfflineGame } from "./offline";
import { GameLoop } from "./loop";
import {
  cleanup,
  createGameplayUi,
  createInput,
  createState,
  GameplayAudio,
  type GameSession,
  type SessionOptions
} from "./session_helpers";
import {
  syncDefeatOverlay,
  syncUpgradeOverlay,
  toggleGameMenuOverlay
} from "./session_overlays";

export async function startOfflineSession(options: SessionOptions): Promise<GameSession> {
  const state = createState();
  const game = new OfflineGame();
  const gameplayAudio = new GameplayAudio(options);

  await game.start(options.profile);

  state.offlineGame = game;

  const screen = createGameScreen("");
  replaceRoot(options.root, screen.element);

  const renderer = new Renderer({ canvas: screen.canvas });
  state.renderer = renderer;
  state.currentSnapshot = game.snapshot() as GameSnapshot | null;
  state.renderSnapshot = state.currentSnapshot;

  const input = createInput(screen.canvas, renderer, state);
  state.input = input;
  input.attach();

  window.addEventListener("keydown", onMenuKeyDown, { passive: false });
  window.addEventListener("resize", onResize);

  state.loop = new GameLoop((frame) => {
    const movement = input.update();

    if (!state.currentSnapshot?.defeated && !state.respawning && !state.menuOpen) {
      game.setInput(movement.active ? movement.x : 0, movement.active ? movement.y : 0);
    } else {
      game.clearInput();
    }

    game.tick(frame.deltaMs);
    state.currentSnapshot = game.snapshot() as GameSnapshot | null;
    state.renderSnapshot = state.currentSnapshot;

    const audioEvents = renderer.render(state.renderSnapshot, createGameplayUi(options, state));
    gameplayAudio.play(audioEvents);
    syncUpgradeOverlay(options, state, "offline");
    syncDefeatOverlay(options, state, "offline");
  });

  state.loop.start();

  return {
    stop: () => {
      cleanup(state);
      window.removeEventListener("keydown", onMenuKeyDown);
      window.removeEventListener("resize", onResize);
    }
  };

  function onMenuKeyDown(event: KeyboardEvent): void {
    if (event.code !== "Escape") {
      return;
    }

    event.preventDefault();
    toggleGameMenuOverlay(options, state);
  }

  function onResize(): void {
    renderer.resize();
  }
}